import React, { useState, useEffect, useCallback } from 'react';
import { fmtDate, fmtShortDate, fmtDateTime } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import {
  Users, Eye, MousePointerClick, FileText, TrendingUp, LogOut,
  Phone, Mail, Search, BarChart3, Activity, UserCheck, Trash2,
  MapPin, ChevronDown, ChevronUp, BookOpen, X, Monitor, Smartphone, Globe, Clock,
  Calculator, DollarSign, Flame, AlertTriangle, Save, PieChart, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AdminEmailView from './admin/AdminEmailView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const safeRequest = (method, url, body) => {
  return fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(res => {
    if (res.status === 401) throw { status: 401 };
    return res.json().catch(() => null);
  });
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'red' }) => {
  const colors = { red: 'bg-red-600', blue: 'bg-blue-600', green: 'bg-emerald-600', amber: 'bg-amber-600', purple: 'bg-purple-600' };
  return (
    <Card className="border-zinc-800 bg-zinc-900" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`${colors[color]} p-2.5 rounded-lg shrink-0`}>
          <Icon className="text-white" size={20} />
        </div>
        <div>
          <p className="text-zinc-400 text-xs uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-white mt-0.5">{value ?? '...'}</p>
          {sub && <p className="text-zinc-500 text-xs mt-1">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const STATUS_OPTIONS = ['pending', 'contacted', 'quoted', 'won', 'lost'];
const STATUS_STYLES = {
  pending: 'bg-yellow-900/40 text-yellow-400 border-yellow-700',
  contacted: 'bg-blue-900/40 text-blue-400 border-blue-700',
  quoted: 'bg-purple-900/40 text-purple-400 border-purple-700',
  won: 'bg-emerald-900/40 text-emerald-400 border-emerald-700',
  lost: 'bg-zinc-800 text-zinc-400 border-zinc-700',
};

const LeadCard = ({ lead, onStatusChange, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  return (
    <Card className="border-zinc-800 bg-zinc-900 overflow-hidden" data-testid={`lead-card-${lead.id}`}>
      <CardContent className="p-0">
        {/* Header Row */}
        <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-red-600/20 flex items-center justify-center shrink-0">
              <span className="text-red-400 font-bold text-sm">{lead.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate">{lead.name}</p>
              <p className="text-zinc-500 text-xs">{fmtDate(lead.submitted_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Status Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowStatusMenu(!showStatusMenu); }}
                className={`text-xs px-2.5 py-1 rounded border ${STATUS_STYLES[lead.status] || STATUS_STYLES.pending} hover:opacity-80 transition-opacity`}
                data-testid={`status-btn-${lead.id}`}
              >
                {lead.status}
              </button>
              {showStatusMenu && (
                <div className="absolute right-0 top-8 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20 py-1 min-w-[120px]" data-testid="status-dropdown">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); onStatusChange(lead.id, s); setShowStatusMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-700 transition-colors ${lead.status === s ? 'text-red-400 font-semibold' : 'text-zinc-300'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Delete */}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(lead.id); }}
              className="text-zinc-600 hover:text-red-500 transition-colors p-1"
              data-testid={`delete-btn-${lead.id}`}
              title="Delete lead"
            >
              <Trash2 size={14} />
            </button>
            {expanded ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="px-4 pb-4 pt-1 border-t border-zinc-800 space-y-3">
            {/* Contact Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <Phone size={13} className="text-zinc-500 shrink-0" />
                <a href={`tel:${lead.phone}`} className="text-zinc-300 text-sm hover:text-red-400 transition-colors">{lead.phone}</a>
              </div>
              {lead.email && (
                <div className="flex items-center gap-2">
                  <Mail size={13} className="text-zinc-500 shrink-0" />
                  <a href={`mailto:${lead.email}`} className="text-zinc-300 text-sm hover:text-red-400 transition-colors truncate">{lead.email}</a>
                </div>
              )}
              {lead.address && (
                <div className="flex items-center gap-2">
                  <MapPin size={13} className="text-zinc-500 shrink-0" />
                  <span className="text-zinc-300 text-sm truncate">{lead.address}</span>
                </div>
              )}
            </div>

            {/* Project Type */}
            {lead.project_type && (
              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Project Type</p>
                <p className="text-zinc-200 text-sm">{lead.project_type}</p>
              </div>
            )}

            {/* Project Details */}
            <div>
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Project Details</p>
              <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap bg-zinc-800/50 rounded-lg p-3">{lead.message}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [pricingConfig, setPricingConfig] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [estimateAnalytics, setEstimateAnalytics] = useState(null);
  const [estimateFilter, setEstimateFilter] = useState('all');
  const [expandedEstimate, setExpandedEstimate] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [dailyVisitors, setDailyVisitors] = useState([]);
  const [leads, setLeads] = useState([]);
  const [blogStats, setBlogStats] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [devices, setDevices] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [salesAnalytics, setSalesAnalytics] = useState(null);
  const [conversionAnalytics, setConversionAnalytics] = useState(null);
  const [salesDashboard, setSalesDashboard] = useState(null);

  const fetchData = useCallback(async (url) => {
    try {
      return await safeRequest('GET', `${API}${url}`);
    } catch (err) {
      if (err.status === 401) { fetch(`${BACKEND_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }).finally(() => navigate('/admin/login')); }
      return null;
    }
  }, [navigate]);

  useEffect(() => {
    const checkAndLoad = async () => {
      const me = await safeRequest('GET', `${API}/admin/me`).catch(() => null);
      if (!me) { navigate('/admin/login'); return; }
      const load = async () => {
      const [ov, pg, cl, dv, ld, bs, ct, dev, ref, ra, pr, est, ea, es, sa, ca, sd] = await Promise.all([
        fetchData('/admin/analytics/overview'),
        fetchData('/admin/analytics/pages'),
        fetchData('/admin/analytics/clicks'),
        fetchData('/admin/analytics/visitors-daily?days=30'),
        fetchData('/admin/leads'),
        fetchData('/admin/blog-stats'),
        fetchData('/admin/contacts'),
        fetchData('/admin/analytics/devices'),
        fetchData('/admin/analytics/referrers'),
        fetchData('/admin/analytics/recent-activity'),
        fetchData('/admin/pricing'),
        fetchData('/admin/estimates'),
        fetchData('/admin/estimate-analytics'),
        fetchData('/admin/email-settings'),
        fetchData('/sales/analytics'),
        fetchData('/sales/conversion-analytics'),
        fetchData('/sales/dashboard'),
      ]);
      if (ov) setOverview(ov);
      if (pg) setPages(pg);
      if (cl) setClicks(cl);
      if (dv) setDailyVisitors(dv);
      if (ld) setLeads(ld);
      if (bs) setBlogStats(bs);
      if (ct) setContacts(ct);
      if (dev) setDevices(dev);
      if (ref) setReferrers(ref);
      if (ra) setRecentActivity(ra);
      if (pr) setPricingConfig(pr);
      if (est) setEstimates(est);
      if (ea) setEstimateAnalytics(ea);
      if (es) setEmailSettings(es);
      if (sa) setSalesAnalytics(sa);
      if (ca) setConversionAnalytics(ca);
      if (sd) setSalesDashboard(sd);
    };
      load();
    };
    checkAndLoad();
  }, [navigate, fetchData]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await safeRequest('PUT', `${API}/admin/leads/${leadId}/status`, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
    } catch {
      // Lead status update failed - server logs capture the error
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await safeRequest('DELETE', `${API}/admin/leads/${leadId}`);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
    } catch {
      // Lead deletion failed - server logs capture the error
    }
  };

  const logout = () => { fetch(`${BACKEND_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }).finally(() => navigate('/admin/login')); };

  const filteredLeads = leads.filter((l) => {
    const q = searchQuery.toLowerCase();
    return !q || l.name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q) || l.phone?.includes(q) || l.project_type?.toLowerCase().includes(q) || l.address?.toLowerCase().includes(q) || l.message?.toLowerCase().includes(q);
  });

  const maxPageViews = pages.length > 0 ? pages[0].views : 1;
  const maxDailyVisitors = dailyVisitors.length > 0 ? Math.max(...dailyVisitors.map((d) => d.visitors)) : 1;

  const [emailSubTab, setEmailSubTab] = useState('mailbox');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'sales', label: 'Sales', icon: PieChart },
    { id: 'estimates', label: 'Estimates', icon: Calculator },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'leads', label: 'Leads', icon: UserCheck },
    { id: 'contacts', label: 'Marketing', icon: BookOpen },
    { id: 'blog', label: 'Blog Stats', icon: FileText },
    { id: 'email', label: 'Email', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="admin-dashboard">
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center">
              <Activity className="text-white" size={16} />
            </div>
            <span className="text-white font-semibold text-sm hidden sm:block">NTX Admin</span>
          </div>
          <nav className="flex items-center gap-1">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  tab === t.id ? 'bg-red-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon size={14} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/sales')} className="text-emerald-400 border-emerald-700 hover:bg-emerald-900/30 hover:text-emerald-300" data-testid="admin-to-sales-btn">
              <TrendingUp size={14} className="mr-1" /> Sales Portal
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white" data-testid="logout-btn">
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ============ OVERVIEW TAB ============ */}
        {tab === 'overview' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Visitors" value={overview?.total_visitors} sub={`${overview?.today_visitors ?? 0} today`} color="blue" />
              <StatCard icon={Eye} label="Page Views" value={overview?.total_pageviews} sub={`${overview?.week_pageviews ?? 0} this week`} color="green" />
              <StatCard icon={MousePointerClick} label="CTA Clicks" value={overview?.total_clicks} color="amber" />
              <StatCard icon={TrendingUp} label="Leads" value={overview?.total_leads} sub={`${overview?.month_leads ?? 0} this month`} color="red" />
            </div>

            {/* Daily Visitors Chart */}
            <Card className="border-zinc-800 bg-zinc-900">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold text-sm mb-4">Daily Visitors (Last 30 Days)</h3>
                {dailyVisitors.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No visitor data yet. Tracking starts when people visit your site.</p>
                ) : (
                  <div>
                    <div className="flex items-end gap-[3px] h-40" data-testid="visitor-chart">
                      {dailyVisitors.map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                          <div
                            className="w-full bg-red-600/80 rounded-t hover:bg-red-500 transition-colors min-h-[2px]"
                            style={{ height: `${maxDailyVisitors > 0 ? Math.max((d.visitors / maxDailyVisitors) * 100, 2) : 2}%` }}
                          />
                          <div className="absolute -top-8 bg-zinc-800 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                            {d.date}: {d.visitors} visitor{d.visitors !== 1 ? 's' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2 text-[10px] text-zinc-600">
                      <span>{dailyVisitors[0]?.date?.slice(5)}</span>
                      <span>{dailyVisitors[Math.floor(dailyVisitors.length / 2)]?.date?.slice(5)}</span>
                      <span>{dailyVisitors[dailyVisitors.length - 1]?.date?.slice(5)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-4">
              {/* Top Pages - What People Are Looking At */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye size={14} className="text-green-400" />
                    <h3 className="text-white font-semibold text-sm">What People Are Looking At</h3>
                  </div>
                  {pages.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No page view data yet.</p>
                  ) : (
                    <div className="space-y-3" data-testid="top-pages">
                      {pages.map((p) => (
                        <div key={p.page}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-zinc-300 truncate mr-2">{p.page === '/' ? 'Homepage' : p.page}</span>
                            <span className="text-zinc-500 shrink-0">{p.views} views</span>
                          </div>
                          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-green-600 rounded-full transition-all" style={{ width: `${(p.views / maxPageViews) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top CTA Clicks - What People Are Clicking */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <MousePointerClick size={14} className="text-amber-400" />
                    <h3 className="text-white font-semibold text-sm">What People Are Clicking</h3>
                  </div>
                  {clicks.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No click data yet. CTA buttons, service cards, phone calls, and video plays are now tracked.</p>
                  ) : (
                    <div className="space-y-2" data-testid="top-clicks">
                      {clicks.map((c) => (
                        <div key={`${c.element}-${c.page}`} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                          <div>
                            <p className="text-zinc-300 text-sm">{c.element}</p>
                            <p className="text-zinc-600 text-xs">{c.page === '/' ? 'Homepage' : c.page}</p>
                          </div>
                          <span className="text-amber-400 font-semibold text-sm">{c.clicks}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              {/* Device Breakdown */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Monitor size={14} className="text-blue-400" />
                    <h3 className="text-white font-semibold text-sm">Devices</h3>
                  </div>
                  {!devices || devices.total === 0 ? (
                    <p className="text-zinc-500 text-sm">No device data yet.</p>
                  ) : (
                    <div className="space-y-3" data-testid="device-breakdown">
                      {[
                        { label: 'Mobile', value: devices.mobile, icon: Smartphone, color: 'bg-blue-500' },
                        { label: 'Desktop', value: devices.desktop, icon: Monitor, color: 'bg-emerald-500' },
                        { label: 'Other', value: devices.other, icon: Globe, color: 'bg-zinc-500' },
                      ].filter(d => d.value > 0).map((d) => (
                        <div key={d.label}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <div className="flex items-center gap-2 text-zinc-300">
                              <d.icon size={14} />
                              <span>{d.label}</span>
                            </div>
                            <span className="text-zinc-400">{d.value} <span className="text-zinc-600 text-xs">({devices.total > 0 ? Math.round((d.value / devices.total) * 100) : 0}%)</span></span>
                          </div>
                          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${d.color}`} style={{ width: `${devices.total > 0 ? (d.value / devices.total) * 100 : 0}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Referrer Sources */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe size={14} className="text-purple-400" />
                    <h3 className="text-white font-semibold text-sm">Traffic Sources</h3>
                  </div>
                  {referrers.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No referrer data yet. Sources appear when visitors come from Google, social media, or other sites.</p>
                  ) : (
                    <div className="space-y-2" data-testid="referrer-sources">
                      {referrers.slice(0, 8).map((r) => (
                        <div key={r.referrer} className="flex items-center justify-between py-1 border-b border-zinc-800/50 last:border-0">
                          <span className="text-zinc-300 text-sm truncate mr-2">{(() => {
                            try { return new URL(r.referrer).hostname; } catch { return r.referrer; }
                          })()}</span>
                          <span className="text-purple-400 font-semibold text-sm shrink-0">{r.visits}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={14} className="text-red-400" />
                    <h3 className="text-white font-semibold text-sm">Recent Activity</h3>
                  </div>
                  {(!recentActivity?.recent_views?.length && !recentActivity?.recent_clicks?.length) ? (
                    <p className="text-zinc-500 text-sm">No recent activity.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="recent-activity">
                      {[
                        ...(recentActivity?.recent_views?.slice(0, 8) || []).map(v => ({
                          type: 'view', text: v.page === '/' ? 'Homepage' : v.page, time: v.timestamp, referrer: v.referrer
                        })),
                        ...(recentActivity?.recent_clicks?.slice(0, 8) || []).map(c => ({
                          type: 'click', text: c.element, time: c.timestamp, page: c.page
                        })),
                      ]
                      .sort((a, b) => (b.time || '').localeCompare(a.time || ''))
                      .slice(0, 12)
                      .map((item, i) => (
                        <div key={`${item.type}-${i}`} className="flex items-start gap-2 py-1.5 border-b border-zinc-800/50 last:border-0">
                          <div className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${item.type === 'view' ? 'bg-green-500' : 'bg-amber-500'}`} />
                          <div className="min-w-0">
                            <p className="text-zinc-300 text-xs truncate">
                              {item.type === 'view' ? `Viewed ${item.text}` : `Clicked "${item.text}"`}
                            </p>
                            <p className="text-zinc-600 text-[10px]">
                              {item.time ? fmtDateTime(item.time) : ''}
                              {item.referrer ? ` · from ${(() => { try { return new URL(item.referrer).hostname; } catch { return 'direct'; } })()}` : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* ============ SALES ANALYTICS TAB ============ */}
        {tab === 'sales' && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard icon={Users} label="Open Opportunities" value={salesDashboard?.open_opportunities ?? 0} sub={`${salesDashboard?.total_customers ?? 0} customers`} color="blue" />
              <StatCard icon={DollarSign} label="Pipeline Value" value={`$${Math.round(salesDashboard?.pipeline_value || 0).toLocaleString()}`} sub={`${salesDashboard?.total_opportunities ?? 0} total opps`} color="green" />
              <StatCard icon={TrendingUp} label="Won Revenue" value={`$${Math.round(salesDashboard?.won_value || 0).toLocaleString()}`} sub={`${salesDashboard?.won ?? 0} deals won`} color="amber" />
              <StatCard icon={Flame} label="Quoted Revenue" value={`$${Math.round(conversionAnalytics?.revenue?.quoted_total || 0).toLocaleString()}`} sub={`${conversionAnalytics?.revenue?.revenue_conversion_pct ?? 0}% conversion`} color="red" />
              <StatCard icon={AlertTriangle} label="Overdue Tasks" value={salesDashboard?.overdue_tasks ?? 0} sub="Need attention" color="purple" />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              {/* Pipeline Funnel */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 size={14} className="text-blue-400" />
                    <h3 className="text-white font-semibold text-sm">Pipeline Funnel</h3>
                  </div>
                  {salesAnalytics?.funnel ? (
                    <div className="space-y-2" data-testid="admin-pipeline-funnel">
                      {salesAnalytics.funnel.filter(s => s.count > 0).map(stage => (
                        <div key={stage.stage}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-zinc-300 capitalize">{stage.stage.replace(/_/g, ' ')}</span>
                            <span className="text-zinc-400">{stage.count} <span className="text-zinc-600 text-xs">({stage.pct}%)</span></span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${Math.max(stage.pct, 2)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-zinc-500 text-sm">Loading pipeline data...</p>}
                </CardContent>
              </Card>

              {/* Conversion Rates */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={14} className="text-green-400" />
                    <h3 className="text-white font-semibold text-sm">Stage Conversion Rates</h3>
                  </div>
                  {conversionAnalytics?.conversion_rates ? (
                    <div className="space-y-2" data-testid="admin-conversion-rates">
                      {Object.entries(conversionAnalytics.conversion_rates)
                        .filter(([, v]) => v > 0)
                        .map(([key, val]) => (
                        <div key={key} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                          <span className="text-zinc-300 text-xs capitalize">{key.replace(/_to_/g, ' → ').replace(/_/g, ' ')}</span>
                          <span className={`text-sm font-bold ${val >= 50 ? 'text-emerald-400' : val >= 20 ? 'text-amber-400' : 'text-red-400'}`}>{val}%</span>
                        </div>
                      ))}
                      {Object.values(conversionAnalytics.conversion_rates).every(v => v === 0) && (
                        <p className="text-zinc-500 text-sm">No stage transitions yet. Data appears as opportunities move through your pipeline.</p>
                      )}
                    </div>
                  ) : <p className="text-zinc-500 text-sm">Loading conversion data...</p>}
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              {/* Revenue Breakdown */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign size={14} className="text-amber-400" />
                    <h3 className="text-white font-semibold text-sm">Revenue Tracker</h3>
                  </div>
                  {conversionAnalytics?.revenue ? (
                    <div className="space-y-3" data-testid="admin-revenue-tracker">
                      {[
                        { label: 'Quoted Total', value: conversionAnalytics.revenue.quoted_total, color: 'text-blue-400' },
                        { label: 'Open Pipeline', value: conversionAnalytics.revenue.open_value, color: 'text-amber-400' },
                        { label: 'Won Revenue', value: conversionAnalytics.revenue.won_value, color: 'text-emerald-400' },
                        { label: 'Lost Revenue', value: conversionAnalytics.revenue.lost_value, color: 'text-red-400' },
                      ].map(r => (
                        <div key={r.label} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                          <span className="text-zinc-300 text-sm">{r.label}</span>
                          <span className={`text-sm font-bold ${r.color}`}>${Math.round(r.value || 0).toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="pt-2 flex items-center justify-between">
                        <span className="text-zinc-400 text-xs font-semibold uppercase">Est-to-Sale Rate</span>
                        <span className="text-lg font-bold text-white">{conversionAnalytics.key_metrics?.estimate_to_sale_pct ?? 0}%</span>
                      </div>
                    </div>
                  ) : <p className="text-zinc-500 text-sm">Loading revenue data...</p>}
                </CardContent>
              </Card>

              {/* Lead Sources */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe size={14} className="text-purple-400" />
                    <h3 className="text-white font-semibold text-sm">Lead Sources</h3>
                  </div>
                  {salesAnalytics?.sources ? (
                    <div className="space-y-2" data-testid="admin-lead-sources">
                      {salesAnalytics.sources.map(src => (
                        <div key={src.source} className="bg-zinc-800/50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-zinc-200 text-sm font-medium capitalize">{src.source.replace(/_/g, ' ')}</span>
                            <span className="text-zinc-400 text-xs">{src.total} leads</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-emerald-400">Won: {src.won}</span>
                            <span className="text-red-400">Lost: {src.lost}</span>
                            <span className="text-blue-400">Open: {src.open}</span>
                            {src.total_value > 0 && <span className="text-amber-400">${Math.round(src.total_value).toLocaleString()}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-zinc-500 text-sm">Loading source data...</p>}
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              {/* Pipeline Forecast */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Calculator size={14} className="text-green-400" />
                    <h3 className="text-white font-semibold text-sm">Weighted Pipeline Forecast</h3>
                  </div>
                  {salesAnalytics?.forecast ? (
                    <div data-testid="admin-pipeline-forecast">
                      <div className="space-y-2 mb-4">
                        {salesAnalytics.forecast.filter(f => f.count > 0).map(f => (
                          <div key={f.stage} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                            <div>
                              <span className="text-zinc-300 text-sm capitalize">{f.stage.replace(/_/g, ' ')}</span>
                              <span className="text-zinc-600 text-xs ml-2">({f.count} deals · {f.probability}% prob)</span>
                            </div>
                            <span className="text-zinc-200 text-sm font-medium">${Math.round(f.weighted_value).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between">
                        <span className="text-zinc-300 text-sm font-semibold">Weighted Total</span>
                        <span className="text-emerald-400 text-lg font-bold">${Math.round(salesAnalytics.forecast_totals?.weighted_pipeline || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  ) : <p className="text-zinc-500 text-sm">Loading forecast...</p>}
                </CardContent>
              </Card>

              {/* Recent Activities */}
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Activity size={14} className="text-red-400" />
                    <h3 className="text-white font-semibold text-sm">Recent CRM Activities</h3>
                  </div>
                  {salesDashboard?.recent_activities?.length > 0 ? (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="admin-recent-crm-activities">
                      {salesDashboard.recent_activities.map((a, i) => (
                        <div key={i} className="flex items-start gap-2 py-1.5 border-b border-zinc-800/50 last:border-0">
                          <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                            a.type === 'quote_signed' ? 'bg-emerald-500' :
                            a.type === 'deposit_update' ? 'bg-blue-500' :
                            a.type === 'email' ? 'bg-amber-500' :
                            a.type === 'call' ? 'bg-purple-500' : 'bg-zinc-600'
                          }`} />
                          <div className="min-w-0">
                            <p className="text-zinc-300 text-xs truncate">{a.description}</p>
                            <p className="text-zinc-600 text-[10px]">
                              {a.type?.replace(/_/g, ' ')} · {a.created_at ? fmtDateTime(a.created_at) : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-zinc-500 text-sm">No CRM activities yet.</p>}
                </CardContent>
              </Card>
            </div>

            {/* AI Impact */}
            {conversionAnalytics?.breakdowns?.ai_impact && (
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Flame size={14} className="text-purple-400" />
                    <h3 className="text-white font-semibold text-sm">AI Impact</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-testid="admin-ai-impact">
                    <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                      <p className="text-zinc-500 text-xs uppercase mb-1">AI-Assisted Deals</p>
                      <p className="text-2xl font-bold text-purple-400">{conversionAnalytics.breakdowns.ai_impact.ai_assisted?.total || 0}</p>
                      <p className="text-zinc-500 text-xs mt-1">Win rate: {conversionAnalytics.breakdowns.ai_impact.ai_assisted?.win_rate || 0}%</p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                      <p className="text-zinc-500 text-xs uppercase mb-1">Non-AI Deals</p>
                      <p className="text-2xl font-bold text-zinc-300">{conversionAnalytics.breakdowns.ai_impact.non_ai?.total || 0}</p>
                      <p className="text-zinc-500 text-xs mt-1">Win rate: {conversionAnalytics.breakdowns.ai_impact.non_ai?.win_rate || 0}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ============ ESTIMATES TAB ============ */}
        {tab === 'estimates' && (
          <>
            {estimateAnalytics && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Calculator} label="Total Estimates" value={estimateAnalytics.total_estimates} sub={`${estimateAnalytics.estimates_this_month} this month`} color="blue" />
                <StatCard icon={DollarSign} label="Pipeline Value" value={`$${Math.round(estimateAnalytics.pipeline_value).toLocaleString()}`} sub={`Avg: $${Math.round(estimateAnalytics.avg_project_value).toLocaleString()}`} color="green" />
                <StatCard icon={Flame} label="Hot Leads" value={estimateAnalytics.hot_leads} sub={`${estimateAnalytics.site_visits_requested} site visits`} color="red" />
                <StatCard icon={TrendingUp} label="Won" value={estimateAnalytics.won} sub={`${estimateAnalytics.conversion_rate}% conversion`} color="amber" />
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {['all', 'HOT', 'WARM', 'STANDARD', 'incomplete'].map(f => (
                <button key={f} onClick={() => setEstimateFilter(f)}
                  className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${estimateFilter === f ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                >{f === 'all' ? 'All' : f === 'incomplete' ? 'Incomplete' : f}</button>
              ))}
            </div>

            <div className="space-y-2" data-testid="estimates-list">
              {estimates
                .filter(e => estimateFilter === 'all' || (estimateFilter === 'incomplete' ? !e.is_complete : e.lead_classification === estimateFilter))
                .map(est => {
                const isExpanded = expandedEstimate === est.id;
                return (
                <Card key={est.id} className="border-zinc-800 bg-zinc-900 overflow-hidden" data-testid={`estimate-card-${est.id}`}>
                  <CardContent className="p-0">
                    {/* Collapsed Header — always visible */}
                    <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors" onClick={() => setExpandedEstimate(isExpanded ? null : est.id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                          est.lead_classification === 'HOT' ? 'bg-red-600/20' : est.lead_classification === 'WARM' ? 'bg-amber-600/20' : 'bg-zinc-700/40'
                        }`}>
                          <span className={`font-bold text-sm ${est.lead_classification === 'HOT' ? 'text-red-400' : est.lead_classification === 'WARM' ? 'text-amber-400' : 'text-zinc-400'}`}>
                            {est.contact?.first_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium text-sm truncate">
                            {est.contact?.first_name} {est.contact?.last_name}
                            {est.customer_range && <span className="text-red-400 ml-2">${est.customer_range.low?.toLocaleString()} – ${est.customer_range.high?.toLocaleString()}</span>}
                          </p>
                          <p className="text-zinc-500 text-xs">{est.services?.join(', ')} · {fmtDate(est.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                            est.lead_classification === 'HOT' ? 'bg-red-600 text-white' :
                            est.lead_classification === 'WARM' ? 'bg-amber-600 text-white' :
                            'bg-zinc-700 text-zinc-300'
                          }`}>{est.lead_classification}</span>
                          {est.needs_review && <AlertTriangle size={12} className="text-amber-400" />}
                          {est.site_visit_requested && <span className="text-[10px] bg-green-900/50 text-green-400 px-1.5 py-0.5 rounded">Site Visit</span>}
                        </div>
                        {isExpanded ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
                      </div>
                    </div>

                    {/* Expanded Detail — all submitted info */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 border-t border-zinc-800 space-y-4">
                        {/* Status + Actions Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            {!est.is_complete && <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded">Incomplete</span>}
                            {est.callback_requested && <span className="text-xs bg-blue-900/50 text-blue-400 px-2 py-0.5 rounded">Callback Requested</span>}
                          </div>
                          <select
                            value={est.status || 'new'}
                            onClick={(e) => e.stopPropagation()}
                            onChange={async (e) => {
                              try {
                                await safeRequest('PUT', `${API}/admin/estimates/${est.id}/status`, { status: e.target.value });
                                setEstimates(prev => prev.map(x => x.id === est.id ? { ...x, status: e.target.value } : x));
                              } catch (err) {
                                if (err.status === 401) { fetch(`${BACKEND_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }).finally(() => navigate('/admin/login')); }
                              }
                            }}
                            className="bg-zinc-800 text-zinc-300 border border-zinc-700 rounded text-xs px-2 py-1"
                          >
                            {['new', 'contacted', 'site_visit_scheduled', 'estimate_in_progress', 'estimate_sent', 'won', 'lost', 'follow_up', 'not_qualified'].map(s => (
                              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                            ))}
                          </select>
                        </div>

                        {/* Contact Info */}
                        <div>
                          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2 font-semibold">Contact Information</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {est.contact?.phone && (
                              <div className="flex items-center gap-2">
                                <Phone size={13} className="text-zinc-500 shrink-0" />
                                <a href={`tel:${est.contact.phone}`} className="text-zinc-300 text-sm hover:text-red-400 transition-colors">{est.contact.phone}</a>
                              </div>
                            )}
                            {est.contact?.email && (
                              <div className="flex items-center gap-2">
                                <Mail size={13} className="text-zinc-500 shrink-0" />
                                <a href={`mailto:${est.contact.email}`} className="text-zinc-300 text-sm hover:text-red-400 transition-colors truncate">{est.contact.email}</a>
                              </div>
                            )}
                            {(est.contact?.address || est.contact?.city) && (
                              <div className="flex items-center gap-2 sm:col-span-2">
                                <MapPin size={13} className="text-zinc-500 shrink-0" />
                                <span className="text-zinc-300 text-sm">
                                  {[est.contact.address, est.contact.city, est.contact.state, est.contact.zip_code].filter(Boolean).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Estimate Details */}
                        <div>
                          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2 font-semibold">Estimate Details</p>
                          <div className="bg-zinc-800/50 rounded-lg p-3 space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Services</span>
                              <span className="text-zinc-200 font-medium">{est.services?.join(', ') || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Estimate Range</span>
                              <span className="text-red-400 font-bold">${est.customer_range?.low?.toLocaleString()} – ${est.customer_range?.high?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-zinc-400">Lead Score</span>
                              <span className="text-zinc-200">{est.lead_score} ({est.lead_classification})</span>
                            </div>
                            {est.timeline && (
                              <div className="flex justify-between">
                                <span className="text-zinc-400">Timeline</span>
                                <span className="text-zinc-200">{est.timeline.replace(/_/g, ' ')}</span>
                              </div>
                            )}
                            {est.budget && (
                              <div className="flex justify-between">
                                <span className="text-zinc-400">Budget</span>
                                <span className="text-zinc-200">{est.budget.replace(/_/g, ' ')}</span>
                              </div>
                            )}
                            {est.hoa && (
                              <div className="flex justify-between">
                                <span className="text-zinc-400">HOA</span>
                                <span className="text-amber-400">Yes — HOA fee applied</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Score Reasons */}
                        {est.lead_score_reasons?.length > 0 && (
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1.5 font-semibold">Score Reasons</p>
                            <div className="flex flex-wrap gap-1.5">
                              {est.lead_score_reasons.map((r, i) => (
                                <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">{r}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Flags */}
                        {est.flags?.length > 0 && (
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1.5 font-semibold">Flags</p>
                            <div className="space-y-1">
                              {est.flags.map((f, i) => (
                                <p key={i} className="text-amber-500 text-xs flex items-center gap-1.5"><AlertTriangle size={11} />{f}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {est.notes && (
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1 font-semibold">Admin Notes</p>
                            <p className="text-zinc-300 text-sm bg-zinc-800/50 rounded-lg p-3">{est.notes}</p>
                          </div>
                        )}

                        <p className="text-zinc-700 text-xs">ID: {est.id} · Submitted: {fmtDateTime(est.created_at)}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );})}
              {estimates.length === 0 && <p className="text-zinc-500 text-sm">No estimates yet. They'll appear here when customers use the Instant Estimate tool.</p>}
            </div>
          </>
        )}

        {/* ============ PRICING TAB ============ */}
        {tab === 'pricing' && pricingConfig && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold text-lg">Pricing Configuration</h2>
              <p className="text-zinc-500 text-xs">Changes take effect immediately for new estimates</p>
            </div>

            {[
              { key: 'fence', label: 'Fence Pricing', fields: [
                { k: 'height_4_rate', l: "4' Fence Rate ($/LF)" }, { k: 'height_5_rate', l: "5' Fence Rate ($/LF)" }, { k: 'height_6_rate', l: "6' Fence Rate ($/LF)" },
                { k: 'rail_3_rate', l: '3 Rail Fence ($/LF)' }, { k: 'rail_4_rate', l: '4 Rail Fence ($/LF)' }, { k: 'rail_5_rate', l: '5 Rail Fence ($/LF)' },
                { k: 'new_pipe_adder', l: 'New Pipe Adder ($/LF)' },
                { k: 'paint_rate', l: 'Paint ($/LF)' }, { k: 'slick_wire_rate', l: 'Slick Wire ($/LF)' }, { k: 'demo_rate', l: 'Demo ($/LF)' },
                { k: 'walk_gate', l: 'Walk Gate ($)' }, { k: 'gate_12', l: "12' Gate ($)" }, { k: 'gate_16', l: "16' Gate ($)" },
                { k: 'ornamental_gate_starting', l: 'Ornamental Gate Starting ($)' },
                { k: 'automation_operator', l: 'Automation Operator ($)' }, { k: 'electrical_allowance', l: 'Electrical Allowance ($)' },
                { k: 'minimum', l: 'Minimum Project ($)' },
                { k: 'terrain_flat', l: 'Terrain: Flat (multiplier)' }, { k: 'terrain_rolling', l: 'Terrain: Rolling (multiplier)' }, { k: 'terrain_difficult', l: 'Terrain: Difficult (multiplier)' },
              ]},
              { key: 'concrete', label: 'Concrete', fields: [
                { k: 'base_rate', l: 'Base Rate ($/SF)' }, { k: 'minimum', l: 'Minimum Project ($)' },
              ]},
              { key: 'building', label: 'Metal Building', fields: [
                { k: 'shell_rate', l: 'Shell Rate ($/SF)' }, { k: 'deluxe_upgrade', l: 'Deluxe Upgrade ($/SF)' }, { k: 'turnkey_upgrade', l: 'Turnkey Upgrade ($/SF)' },
                { k: 'lean_to_rate', l: 'Lean-To Rate ($/SF)' }, { k: 'concrete_rate', l: 'Concrete Rate ($/SF)' },
                { k: 'height_12_multiplier', l: '12\' Height (multiplier)' }, { k: 'height_14_multiplier', l: '14\' Height (multiplier)' }, { k: 'height_16_multiplier', l: '16\' Height (multiplier)' },
                { k: 'minimum', l: 'Minimum Project ($)' },
              ]},
              { key: 'clearing', label: 'Land Clearing', fields: [
                { k: 'light_rate', l: 'Light ($/acre)' }, { k: 'medium_rate', l: 'Medium ($/acre)' }, { k: 'heavy_rate', l: 'Heavy ($/acre)' },
                { k: 'hourly_machine', l: 'Machine Rate ($/hr)' }, { k: 'daily_machine', l: 'Machine Rate ($/day)' },
                { k: 'minimum', l: 'Minimum Project ($)' },
              ]},
              { key: 'dirt_work', label: 'Dirt / Site Work', fields: [
                { k: 'light_grading_rate', l: 'Light Grading ($/SF)' }, { k: 'pad_prep_rate', l: 'Pad Prep ($/SF)' },
                { k: 'hourly_rate', l: 'Hourly Rate ($)' }, { k: 'daily_rate', l: 'Daily Rate ($)' },
              ]},
              { key: 'gravel', label: 'Gravel', fields: [
                { k: 'standard_rate', l: 'Standard Gravel ($/SF)' }, { k: 'premium_black_rate', l: 'Premium Black ($/SF)' },
                { k: 'minimum', l: 'Minimum Project ($)' },
              ]},
              { key: 'travel', label: 'Travel / Mobilization', fields: [
                { k: 'tier_1_miles', l: 'Tier 1 Max Miles' }, { k: 'tier_1_cost', l: 'Tier 1 Cost ($)' },
                { k: 'tier_2_miles', l: 'Tier 2 Max Miles' }, { k: 'tier_2_cost', l: 'Tier 2 Cost ($)' },
                { k: 'tier_3_miles', l: 'Tier 3 Max Miles' }, { k: 'tier_3_cost', l: 'Tier 3 Cost ($)' },
                { k: 'per_diem_daily', l: 'Per Diem ($/person/day)' },
              ]},
              { key: 'general', label: 'Estimate Settings', fields: [
                { k: 'hoa_fee', l: 'HOA Fee ($)' }, { k: 'low_multiplier', l: 'Low Estimate Multiplier' }, { k: 'high_multiplier', l: 'High Estimate Multiplier' },
                { k: 'rounding_increment', l: 'Rounding Increment ($)' },
                { k: 'package_3_discount', l: '3-Service Discount %' }, { k: 'package_4_discount', l: '4-Service Discount %' }, { k: 'package_5_discount', l: '5+ Service Discount %' },
              ]},
            ].map(section => (
              <Card key={section.key} className="border-zinc-800 bg-zinc-900" data-testid={`pricing-${section.key}`}>
                <CardContent className="p-5">
                  <h3 className="text-white font-semibold text-sm mb-4">{section.label}</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {section.fields.map(field => (
                      <div key={field.k}>
                        <label className="text-zinc-400 text-xs block mb-1">{field.l}</label>
                        <Input
                          type="number"
                          step="any"
                          value={pricingConfig[section.key]?.[field.k] ?? ''}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setPricingConfig(prev => ({
                              ...prev,
                              [section.key]: { ...prev[section.key], [field.k]: isNaN(val) ? '' : val }
                            }));
                          }}
                          className="bg-zinc-800 border-zinc-700 text-white text-sm h-8"
                        />
                      </div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    className="mt-4 bg-red-600 hover:bg-red-700 text-white gap-1.5"
                    disabled={pricingSaving}
                    onClick={async () => {
                      setPricingSaving(true);
                      try {
                        await safeRequest('PUT', `${API}/admin/pricing/${section.key}`, { updates: pricingConfig[section.key] });
                      } catch (e) {
                        if (e.status === 401) { fetch(`${BACKEND_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }).finally(() => navigate('/admin/login')); }
                        else console.error(e);
                      }
                      setPricingSaving(false);
                    }}
                  >
                    <Save size={12} /> Save {section.label}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </>
        )}


        {/* ============ LEADS TAB ============ */}
        {tab === 'leads' && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h2 className="text-white font-semibold text-lg">Quote Requests ({leads.length})</h2>
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={14} />
                <Input
                  placeholder="Search name, email, phone, address, details..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-zinc-900 border-zinc-700 text-white text-sm placeholder:text-zinc-500 h-9"
                  data-testid="lead-search"
                />
              </div>
            </div>

            {filteredLeads.length === 0 ? (
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-8 text-center">
                  <p className="text-zinc-500">No leads found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="leads-list">
                {filteredLeads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onStatusChange={updateLeadStatus}
                    onDelete={deleteLead}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ============ MARKETING CONTACTS TAB ============ */}
        {tab === 'contacts' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Contacts" value={contacts?.total_contacts ?? 0} color="blue" />
              <StatCard icon={Mail} label="Email Addresses" value={contacts?.total_emails ?? 0} color="green" />
              <StatCard icon={Phone} label="Phone Numbers" value={contacts?.total_phones ?? 0} color="amber" />
              <StatCard icon={MapPin} label="Addresses" value={contacts?.total_addresses ?? 0} color="purple" />
            </div>

            <Card className="border-zinc-800 bg-zinc-900">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold text-sm mb-4">Contact Directory</h3>
                {!contacts?.contacts?.length ? (
                  <p className="text-zinc-500 text-sm">No contacts collected yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-zinc-800">
                    <table className="w-full text-sm" data-testid="contacts-table">
                      <thead>
                        <tr className="bg-zinc-800 text-zinc-400 text-xs uppercase tracking-wider">
                          <th className="text-left px-4 py-3">Name</th>
                          <th className="text-left px-4 py-3">Email</th>
                          <th className="text-left px-4 py-3">Phone</th>
                          <th className="text-left px-4 py-3 hidden md:table-cell">Location</th>
                          <th className="text-left px-4 py-3 hidden lg:table-cell">Project Type</th>
                          <th className="text-left px-4 py-3 hidden sm:table-cell">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800">
                        {contacts.contacts.map((c) => (
                          <tr key={`${c.name}-${c.submitted_at}`} className="hover:bg-zinc-800/40 transition-colors">
                            <td className="px-4 py-3 text-white font-medium whitespace-nowrap">{c.name}</td>
                            <td className="px-4 py-3">
                              {c.email ? (
                                <a href={`mailto:${c.email}`} className="text-red-400 hover:text-red-300 text-xs transition-colors">{c.email}</a>
                              ) : <span className="text-zinc-600 text-xs">—</span>}
                            </td>
                            <td className="px-4 py-3">
                              <a href={`tel:${c.phone}`} className="text-zinc-300 hover:text-red-400 text-xs transition-colors whitespace-nowrap">{c.phone}</a>
                            </td>
                            <td className="px-4 py-3 text-zinc-400 text-xs hidden md:table-cell max-w-[200px] truncate">{c.address || '—'}</td>
                            <td className="px-4 py-3 text-zinc-400 text-xs hidden lg:table-cell">{c.project_type || '—'}</td>
                            <td className="px-4 py-3 text-zinc-500 text-xs hidden sm:table-cell whitespace-nowrap">
                              {c.submitted_at ? fmtDate(c.submitted_at) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ============ BLOG STATS TAB ============ */}
        {tab === 'blog' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <StatCard icon={FileText} label="Published Posts" value={blogStats?.posts?.length ?? 0} color="purple" />
              <StatCard icon={Eye} label="Total Blog Views" value={blogStats?.total_views ?? 0} color="green" />
            </div>

            <Card className="border-zinc-800 bg-zinc-900">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold text-sm mb-4">Blog Posts by Views</h3>
                {!blogStats?.posts?.length ? (
                  <p className="text-zinc-500 text-sm">No blog posts yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="blog-stats-list">
                    {blogStats.posts.map((post) => (
                      <div key={post.slug} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                        <div className="flex-1 mr-4">
                          <p className="text-zinc-300 text-sm font-medium truncate">{post.title}</p>
                          <p className="text-zinc-600 text-xs">
                            {fmtShortDate(post.generated_at)} · {post.word_count} words
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 text-zinc-400">
                          <Eye size={14} />
                          <span className="font-semibold text-sm">{post.views}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ============ EMAIL SETTINGS TAB ============ */}
        {tab === 'email' && (
          <>
            {/* Email Sub-tab Switcher */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setEmailSubTab('mailbox')}
                className={`px-4 py-2 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                  emailSubTab === 'mailbox' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
                }`}
                data-testid="email-subtab-mailbox"
              >
                <Mail size={14} /> Mailbox
              </button>
              <button
                onClick={() => setEmailSubTab('settings')}
                className={`px-4 py-2 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                  emailSubTab === 'settings' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
                }`}
                data-testid="email-subtab-settings"
              >
                <Settings size={14} /> Settings
              </button>
            </div>

            {emailSubTab === 'mailbox' ? (
              <AdminEmailView />
            ) : (
              <EmailSettingsTab
            settings={emailSettings}
            saving={emailSaving}
            testResult={emailTestResult}
            testEmailAddr={testEmailAddr}
            onTestEmailChange={setTestEmailAddr}
            onSave={async (updates) => {
              setEmailSaving(true);
              setEmailTestResult(null);
              try {
                const result = await safeRequest('PUT', `${API}/admin/email-settings`, updates);
                if (result) setEmailSettings(result);
                setEmailSaving(false);
                return true;
              } catch {
                setEmailSaving(false);
                return false;
              }
            }}
            onSendTest={async () => {
              if (!testEmailAddr) return;
              setEmailTestResult({ status: 'sending' });
              try {
                const result = await safeRequest('POST', `${API}/admin/email-settings/test`, { to_email: testEmailAddr });
                setEmailTestResult(result);
              } catch {
                setEmailTestResult({ status: 'error', error: 'Request failed' });
              }
            }}
          />
            )}
          </>
        )}
      </main>
    </div>
  );
};

/* ============ EMAIL SETTINGS COMPONENT ============ */
const EmailSettingsTab = ({ settings, saving, testResult, testEmailAddr, onTestEmailChange, onSave, onSendTest }) => {
  const [apiKey, setApiKey] = useState('');
  const [provider, setProvider] = useState('test');
  const [showApiKey, setShowApiKey] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (settings) {
      setProvider(settings.provider || 'test');
    }
  }, [settings]);

  const handleSave = async () => {
    const updates = { provider };
    if (apiKey) updates.resend_api_key = apiKey;
    const ok = await onSave(updates);
    if (ok) {
      setSaveSuccess(true);
      setApiKey('');
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const isLive = settings?.provider === 'resend' && settings?.resend_api_key_set;

  return (
    <div className="space-y-6" data-testid="email-settings-tab">
      {/* Status Banner */}
      <div className={`rounded-lg border p-4 flex items-start gap-3 ${isLive ? 'bg-emerald-950/30 border-emerald-800' : 'bg-amber-950/30 border-amber-800'}`}>
        <div className={`w-3 h-3 rounded-full mt-0.5 ${isLive ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
        <div>
          <p className={`font-semibold text-sm ${isLive ? 'text-emerald-400' : 'text-amber-400'}`}>
            {isLive ? 'Email System LIVE' : 'Email System in TEST MODE'}
          </p>
          <p className="text-zinc-400 text-xs mt-1">
            {isLive
              ? 'Emails are being sent via Resend from @ntxpipefence.com addresses.'
              : 'Emails are logged but NOT actually sent. Configure Resend below to go live.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Key Configuration */}
        <Card className="border-zinc-800 bg-zinc-900">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <Mail size={16} className="text-red-400" /> Resend Configuration
            </h3>

            <div>
              <label className="text-zinc-400 text-xs block mb-1.5">Provider</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setProvider('test')}
                  className={`px-4 py-2 rounded text-xs font-medium transition-colors ${provider === 'test' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                  data-testid="provider-test-btn"
                >
                  Test Mode
                </button>
                <button
                  onClick={() => setProvider('resend')}
                  className={`px-4 py-2 rounded text-xs font-medium transition-colors ${provider === 'resend' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                  data-testid="provider-resend-btn"
                >
                  Resend (Live)
                </button>
              </div>
            </div>

            <div>
              <label className="text-zinc-400 text-xs block mb-1.5">Resend API Key</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder={settings?.resend_api_key_set ? '••••••••••••• (key is set)' : 'Enter your Resend API key'}
                    className="bg-zinc-800 border-zinc-700 text-white text-sm pr-10"
                    data-testid="resend-api-key-input"
                  />
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 text-xs"
                  >
                    {showApiKey ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              {settings?.resend_api_key_set && (
                <p className="text-emerald-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> API key is configured
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white text-xs"
                data-testid="save-email-settings-btn"
              >
                <Save size={14} className="mr-1" />
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
              {saveSuccess && <span className="text-emerald-400 text-xs">Settings saved!</span>}
            </div>
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card className="border-zinc-800 bg-zinc-900">
          <CardContent className="p-5 space-y-4">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-400" /> Send Test Email
            </h3>
            <p className="text-zinc-400 text-xs">
              Send a test email to verify your configuration. If in test mode, the email will be logged but not actually delivered.
            </p>
            <div>
              <label className="text-zinc-400 text-xs block mb-1.5">Recipient Email</label>
              <Input
                type="email"
                value={testEmailAddr}
                onChange={e => onTestEmailChange(e.target.value)}
                placeholder="your-email@example.com"
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                data-testid="test-email-input"
              />
            </div>
            <Button
              onClick={onSendTest}
              disabled={!testEmailAddr || testResult?.status === 'sending'}
              className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs"
              data-testid="send-test-email-btn"
            >
              {testResult?.status === 'sending' ? 'Sending...' : 'Send Test Email'}
            </Button>
            {testResult && testResult.status !== 'sending' && (
              <div className={`rounded p-3 text-xs ${testResult.status === 'sent' ? 'bg-emerald-950/30 border border-emerald-800 text-emerald-400' : testResult.status === 'test_logged' ? 'bg-amber-950/30 border border-amber-800 text-amber-400' : 'bg-red-950/30 border border-red-800 text-red-400'}`}
                data-testid="test-email-result"
              >
                {testResult.status === 'sent' && 'Email sent successfully! Check your inbox.'}
                {testResult.status === 'test_logged' && 'Email logged in test mode (not actually sent). Switch to Resend provider to send live.'}
                {testResult.status === 'failed' && `Send failed: ${testResult.error}`}
                {testResult.status === 'error' && `Error: ${testResult.error}`}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Domain Verification Guide */}
      <Card className="border-zinc-800 bg-zinc-900">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-white font-semibold text-sm">Domain Verification Setup Guide</h3>
          <p className="text-zinc-400 text-xs">
            To send emails from <strong className="text-white">@ntxpipefence.com</strong>, you need to verify the domain with Resend and add DNS records at IONOS.
          </p>
          <div className="space-y-3">
            <StepItem num={1} title="Create a Resend Account" desc='Go to resend.com and sign up for a free account.' link="https://resend.com/signup" />
            <StepItem num={2} title="Add Your Domain" desc='In Resend Dashboard → Domains → Add Domain → enter "ntxpipefence.com".' />
            <StepItem num={3} title="Copy DNS Records" desc="Resend will show you 3 DNS records (SPF, DKIM, DMARC) that you need to add." />
            <StepItem num={4} title="Add Records at IONOS" desc='Log into IONOS → Domains → DNS Settings → Add each record Resend provided (TXT & CNAME types).' link="https://my.ionos.com" />
            <StepItem num={5} title="Verify in Resend" desc='Click "Verify" in Resend. DNS propagation takes up to 48 hours but usually works within minutes.' />
            <StepItem num={6} title="Get Your API Key" desc='Resend Dashboard → API Keys → Create API Key → paste it in the field above.' />
            <StepItem num={7} title='Switch to "Resend (Live)"' desc='Select the Resend provider above and save. Then send a test email to confirm everything works.' />
          </div>
        </CardContent>
      </Card>

      {/* Current Settings Summary */}
      {settings && (
        <Card className="border-zinc-800 bg-zinc-900">
          <CardContent className="p-5">
            <h3 className="text-white font-semibold text-sm mb-3">Current Configuration</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div><span className="text-zinc-500 block">Provider</span><span className="text-white font-medium">{settings.provider}</span></div>
              <div><span className="text-zinc-500 block">Domain</span><span className="text-white font-medium">{settings.approved_domain}</span></div>
              <div><span className="text-zinc-500 block">Default Sender</span><span className="text-white font-medium">{settings.default_sender_email}</span></div>
              <div><span className="text-zinc-500 block">API Key Set</span><span className={`font-medium ${settings.resend_api_key_set ? 'text-emerald-400' : 'text-amber-400'}`}>{settings.resend_api_key_set ? 'Yes' : 'No'}</span></div>
              <div><span className="text-zinc-500 block">Attachment Threshold</span><span className="text-white font-medium">{Math.round((settings.attachment_threshold_bytes || 0) / 1024 / 1024)} MB</span></div>
              <div><span className="text-zinc-500 block">File Link Expiry</span><span className="text-white font-medium">{settings.file_link_expiration_hours || 168} hours</span></div>
              <div><span className="text-zinc-500 block">Tracking</span><span className="text-white font-medium">{settings.tracking_enabled ? 'Enabled' : 'Disabled'}</span></div>
              <div><span className="text-zinc-500 block">Last Updated</span><span className="text-white font-medium">{settings.updated_at ? fmtDate(settings.updated_at) : 'Never'}</span></div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const StepItem = ({ num, title, desc, link }) => (
  <div className="flex gap-3 items-start">
    <div className="w-6 h-6 rounded-full bg-red-600/20 flex items-center justify-center shrink-0 mt-0.5">
      <span className="text-red-400 text-xs font-bold">{num}</span>
    </div>
    <div>
      <p className="text-white text-sm font-medium">{title}</p>
      <p className="text-zinc-400 text-xs mt-0.5">
        {desc}
        {link && <> — <a href={link} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300 underline">{link.replace('https://', '')}</a></>}
      </p>
    </div>
  </div>
);

export default AdminDashboard;
